import { useMemo } from 'react';

import { useGoogleCatalogAttributes } from '~/features/content-enrichment/hooks/use-google-catalog-attributes';

import { adaptGoogleCatalog } from './adapt-google-catalog';
import type { EnrichmentAttributeGroup } from './types';

/**
 * The enrichment-run panel's Google attribute taxonomy, from the google agent's own
 * catalog (`GET google-agent/catalog/content-attributes`).
 *
 * Previously the config-service fetch the experiment wizard owns. It moved because the
 * taxonomy was never really config: nothing wrote those rows but a literal list in
 * config's seed script, whose source of truth was this agent's catalog all along. The
 * wizard still uses the config payload, so `useContentAttributes` stays where it is.
 *
 * The full curated taxonomy is surfaced — mandatory attributes (e.g. Google Product
 * Category) included — so the creation and delivery panels see the same groups.
 *
 * `enabled` defers the request until the panel actually opens; the groups are never
 * needed before then.
 */
export function useEnrichmentAttributeGroups(enabled: boolean): {
  groups: EnrichmentAttributeGroup[];
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const query = useGoogleCatalogAttributes(enabled);

  const groups = useMemo(() => (query.data ? adaptGoogleCatalog(query.data) : []), [query.data]);

  return {
    groups,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => {
      void query.refetch();
    },
  };
}
